'use client';

import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';

import {
  fetchInvoices,
  fetchReceipts,
  fetchAuthorizations,
  fetchApplications,
  fetchNotifications,
} from '@/redux/customer/thunks';

import {
  selectInvoicesMeta,
  selectReceiptsMeta,
  selectAuthorizationsState,
  selectApplicationsState,
  selectNotificationsState,
} from '@/redux/customer/selectors';

type Paginated = {
  count?: number;
  next?: string | null;
  previous?: string | null;
};

type UserMenuCounts = {
  invoicesCount: number;
  receiptsCount: number;
  applicationsCount: number;
  authorizationsCount: number;
  notificationsCount: number;
  loading: boolean;
  error: string | null;
};

export const useUserMenuCounts = (): UserMenuCounts => {
  const dispatch = useAppDispatch();

  const invoicesPage = useAppSelector(selectInvoicesMeta) as
    | Paginated
    | undefined;
  const receiptsPage = useAppSelector(selectReceiptsMeta) as
    | Paginated
    | undefined;
  const authorizationsPage = useAppSelector(selectAuthorizationsState) as
    | Paginated
    | undefined;
  const applicationsPage = useAppSelector(selectApplicationsState) as
    | Paginated
    | undefined;
  const notificationsPage = useAppSelector(selectNotificationsState) as
    | Paginated
    | undefined;

  useEffect(() => {
    void dispatch(fetchInvoices({ page: 1, page_size: 1 }));
    void dispatch(fetchReceipts({ page: 1, page_size: 1 }));
    void dispatch(fetchAuthorizations({ page: 1, page_size: 1 }));
    void dispatch(fetchApplications({ page: 1, page_size: 1 }));
    void dispatch(fetchNotifications({ page: 1, page_size: 1 }));
  }, [dispatch]);

  const result = useMemo(() => {
    const invoicesCount = invoicesPage?.count ?? 0;
    const receiptsCount = receiptsPage?.count ?? 0;
    const applicationsCount = applicationsPage?.count ?? 0;
    const authorizationsCount = authorizationsPage?.count ?? 0;
    const notificationsCount = notificationsPage?.count ?? 0;

    const loading = false;
    const error: string | null = null;

    return {
      invoicesCount,
      receiptsCount,
      applicationsCount,
      authorizationsCount,
      notificationsCount,
      loading,
      error,
    };
  }, [
    invoicesPage,
    receiptsPage,
    applicationsPage,
    authorizationsPage,
    notificationsPage,
  ]);

  return result;
};
